/**
 * Owner: Masa Bayu
 * File: qxcircle-api.ts
 * Date:Time: 2026-09-18 11:11
 * Description: QXCircle client — local engine bridge · guest-safe · no mock account.
 */

export * from "./qxcircle-api-types";
import type {
  CreateQxCirclePostMediaInput,
  QxCircleAuthorProfile,
  QxCircleBlockPayload,
  QxCircleBlocksResponse,
  QxCircleBookmarksResponse,
  QxCircleComment,
  QxCircleCommentsResponse,
  QxCircleComposeDraft,
  QxCircleFeedFilter,
  QxCircleFeedPost,
  QxCircleFeedResponse,
  QxCircleFollowPayload,
  QxCircleMentionFeedResponse,
  QxCircleMutePayload,
  QxCircleMutesResponse,
  QxCircleNotification,
  QxCircleNotificationsResponse,
  QxCircleOverview,
  QxCirclePostDetailResponse,
  QxCircleReportPayload,
  QxCircleSearchResponse,
  QxCircleSharePayload,
  QxCircleTagFeedResponse,
  QxCircleTrendingTagsResponse,
} from "./qxcircle-api-types";

import type { AccountBundle } from "../account/account-types";
import {
  getQxCircleEngine,
  getQxCircleLocalAccount,
  getQxCircleLocalAccountId,
  getQxCircleSessionHandle,
} from "../engine/local-runtime";
import {
  UnauthorizedException,
  type HttpException,
} from "../engine/_nestjs-common";

function wrap<T>(run: () => T): Promise<T> {
  try {
    return Promise.resolve(run());
  } catch (e) {
    const err = e as HttpException & { status?: number; message?: string };
    if (err && typeof err.status === "number") {
      if (err.status === 401 || err.status === 403) {
        return Promise.reject(new Error("Sign in to use QXCircle"));
      }
      return Promise.reject(new Error(err.message || `QXCircle API ${err.status}`));
    }
    return Promise.reject(e);
  }
}

function requireAccount(): AccountBundle {
  const account = getQxCircleLocalAccount();
  if (account === null) {
    throw new UnauthorizedException("Sign in to use QXCircle");
  }
  return account;
}

function requireAccountId(): string {
  return requireAccount().user.id;
}

export function getQxCircleOverview(): Promise<QxCircleOverview> {
  return wrap(() => getQxCircleEngine().overview());
}

export function getQxCircleFeed(
  filter: QxCircleFeedFilter = "all",
): Promise<QxCircleFeedResponse> {
  return wrap(() =>
    getQxCircleEngine().feed(filter, getQxCircleLocalAccountId()),
  );
}

export function getQxCirclePost(
  postId: string,
): Promise<QxCirclePostDetailResponse> {
  return wrap(() =>
    getQxCircleEngine().getPost(postId, getQxCircleLocalAccountId()),
  );
}

export function createQxCirclePost(
  body: string,
  media?: CreateQxCirclePostMediaInput | null,
): Promise<QxCircleFeedPost> {
  return wrap(() =>
    getQxCircleEngine().createPost(
      { body, media: media ?? null },
      requireAccount(),
    ),
  );
}

export function updateQxCirclePost(
  postId: string,
  input: { body: string; clearMedia?: boolean },
): Promise<QxCircleFeedPost> {
  return wrap(() =>
    getQxCircleEngine().updatePost(postId, input, requireAccountId()),
  );
}

export function deleteQxCirclePost(
  postId: string,
): Promise<{ ok: true; id: string }> {
  return wrap(() =>
    getQxCircleEngine().deletePost(postId, requireAccountId()),
  );
}

export function likeQxCirclePost(postId: string): Promise<QxCircleFeedPost> {
  return wrap(() => getQxCircleEngine().likePost(postId));
}

export function bookmarkQxCirclePost(
  postId: string,
): Promise<QxCircleFeedPost> {
  return wrap(() => getQxCircleEngine().bookmarkPost(postId));
}

export function getQxCircleBookmarks(): Promise<QxCircleBookmarksResponse> {
  return wrap(() => getQxCircleEngine().listBookmarks());
}

export function getQxCircleTagFeed(tag: string): Promise<QxCircleTagFeedResponse> {
  return wrap(() => getQxCircleEngine().tagFeed(tag));
}

export function getQxCircleMentionFeed(
  handle: string,
): Promise<QxCircleMentionFeedResponse> {
  return wrap(() => getQxCircleEngine().mentionFeed(handle));
}

export function getQxCircleTrendingTags(): Promise<QxCircleTrendingTagsResponse> {
  return wrap(() => getQxCircleEngine().trendingTags());
}

export function shareQxCirclePost(
  postId: string,
): Promise<QxCircleSharePayload> {
  return wrap(() => {
    const result = getQxCircleEngine().sharePost(postId);
    return {
      postId: result.postId,
      sharePath: result.sharePath,
      shareUrl: result.shareUrl,
      title: result.title,
      text: result.text,
      authorName: result.authorName,
      shareCount: result.shareCount,
    };
  });
}

export function followQxCircleAuthor(
  authorHandle: string,
): Promise<QxCircleFollowPayload> {
  return wrap(() => {
    const result = getQxCircleEngine().followAuthor(
      authorHandle,
      getQxCircleSessionHandle(),
    );
    return {
      authorHandle: result.authorHandle,
      authorName: result.authorName,
      followedByLocal: result.followedByLocal,
      note: result.note,
    };
  });
}

export function blockQxCircleAuthor(
  authorHandle: string,
): Promise<QxCircleBlockPayload> {
  return wrap(() => {
    const result = getQxCircleEngine().blockAuthor(
      authorHandle,
      getQxCircleSessionHandle(),
    );
    return {
      authorHandle: result.authorHandle,
      authorName: result.authorName,
      blockedByLocal: result.blockedByLocal,
      note: result.note,
    };
  });
}

export function getQxCircleBlocks(): Promise<QxCircleBlocksResponse> {
  return wrap(() => getQxCircleEngine().listBlocks());
}

export function muteQxCircleAuthor(
  authorHandle: string,
): Promise<QxCircleMutePayload> {
  return wrap(() => {
    const result = getQxCircleEngine().muteAuthor(
      authorHandle,
      getQxCircleSessionHandle(),
    );
    return {
      authorHandle: result.authorHandle,
      authorName: result.authorName,
      mutedByLocal: result.mutedByLocal,
      note: result.note,
    };
  });
}

export function getQxCircleMutes(): Promise<QxCircleMutesResponse> {
  return wrap(() => getQxCircleEngine().listMutes());
}

export function getQxCircleComments(
  postId: string,
): Promise<QxCircleCommentsResponse> {
  return wrap(() =>
    getQxCircleEngine().listComments(postId, getQxCircleLocalAccountId()),
  );
}

export function createQxCircleComment(
  postId: string,
  body: string,
  parentId?: string | null,
): Promise<QxCircleComment> {
  return wrap(() =>
    getQxCircleEngine().createComment(
      postId,
      { body, parentId: parentId ?? null },
      requireAccount(),
    ),
  );
}

export function getQxCircleAuthorProfile(
  authorHandle: string,
): Promise<QxCircleAuthorProfile> {
  return wrap(() => getQxCircleEngine().authorProfile(authorHandle));
}

export function getQxCircleNotifications(): Promise<QxCircleNotificationsResponse> {
  return wrap(() => getQxCircleEngine().listNotifications());
}

export function markQxCircleNotificationRead(
  notificationId: string,
): Promise<QxCircleNotification> {
  return wrap(() => getQxCircleEngine().markNotificationRead(notificationId));
}

export function markAllQxCircleNotificationsRead(): Promise<QxCircleNotificationsResponse> {
  return wrap(() => getQxCircleEngine().markAllNotificationsRead());
}

export function searchQxCircle(query: string): Promise<QxCircleSearchResponse> {
  return wrap(() => getQxCircleEngine().search(query));
}

export function reportQxCirclePost(
  postId: string,
  reason: string,
): Promise<QxCircleReportPayload> {
  return wrap(() => {
    const result = getQxCircleEngine().reportPost(postId, reason);
    return {
      postId: result.postId,
      reason: result.reason,
      status: result.status,
      note: result.note,
    };
  });
}

export function getQxCircleComposeDraft(): Promise<QxCircleComposeDraft> {
  return wrap(() => getQxCircleEngine().getComposeDraft());
}

export function saveQxCircleComposeDraft(
  body: string,
): Promise<QxCircleComposeDraft> {
  return wrap(() => getQxCircleEngine().saveComposeDraft(body));
}

export function clearQxCircleComposeDraft(): Promise<QxCircleComposeDraft> {
  return wrap(() => getQxCircleEngine().clearComposeDraft());
}
